/**
 * bossPhase2Constants.ts
 * Phase 2 tunables for the Void Mothership — the shield timed-window
 * cycle (`SHIELDED` ↔ `EXPOSED`) and the EXPOSED-hit flash.
 *
 * Extracted from `bossConstants.ts` to keep that module under the 300-
 * line file budget. Constants for Phase 1 (turret + drone spawn) remain
 * in `bossConstants.ts`; Phase 3 (ram + missile barrage) lives in
 * `bossPhase3Constants.ts`; this file owns the Phase 2 shield cycle only.
 *
 * `bossConstants.ts` re-exports every symbol from this module so
 * existing call sites continue to import from `./bossConstants`
 * without churn — new code is free to import directly from here.
 *
 * NO React imports, NO Skia imports.
 *
 * @module bossPhase2Constants
 */

// ── Phase 2: shield timed-window cycle ─────────────────────────────────

/**
 * Seconds the boss stays in its `SHIELDED` sub-phase per cycle while in
 * HOLD. During `SHIELDED`, `takeDamage()` is a no-op — ship projectiles
 * still collide (and are consumed) but no HP is removed. Sized to feel
 * like a real "wait for the window" beat rather than a momentary blink:
 * roughly two-thirds of the cycle is shielded so the player has time to
 * read the tell before the opening.
 */
export const VOID_MOTHERSHIP_SHIELDED_DURATION_S = 4.0;

/**
 * Seconds the boss stays in its `EXPOSED` sub-phase per cycle while in
 * HOLD. The damage window. Short enough that the player can't park a
 * laser stream through the boss's whole life, long enough to deliver a
 * handful of hits at the current 0.15 s ship cooldown.
 */
export const VOID_MOTHERSHIP_EXPOSED_DURATION_S = 2.0;

/**
 * Initial delay (s) between reaching HOLD and the first `SHIELDED`→
 * `EXPOSED` flip. The boss starts HOLD in `SHIELDED` so the player
 * registers the new threat (turret + drones) before any damage window
 * opens. Set to the full shielded duration so the first window arrives
 * on the same cadence as every subsequent one — no special-cased opener.
 */
export const VOID_MOTHERSHIP_SHIELD_RAMP_IN_S =
  VOID_MOTHERSHIP_SHIELDED_DURATION_S;

/**
 * Angular speed (radians per second) of the shield-ring rotation visual.
 * The boss accumulates `shieldAngle` at this rate every tick regardless
 * of sub-phase so the ring spins continuously across SHIELDED ↔ EXPOSED
 * flips (i.e. when the shield reappears it picks up where it left off,
 * not at a hard zero). One full revolution every ~6.3 s — visible
 * motion without competing with the faster combat reads (turret bolt,
 * ram telegraph). Renderer-only — does not affect damage gating.
 */
export const VOID_MOTHERSHIP_SHIELD_ROTATION_RAD_S = 1.0;

/**
 * Number of node rects rendered around the boss while SHIELDED. Four
 * gives an unambiguous "rotating ring" read at the current `..._NODE_SIZE`
 * — three reads as a triangle, five+ starts blurring into a continuous
 * band at the 0.25 s missile-cadence frame budget.
 */
export const VOID_MOTHERSHIP_SHIELD_NODE_COUNT = 4;

/** Side length (px, world-space) of each shield node's AABB. */
export const VOID_MOTHERSHIP_SHIELD_NODE_SIZE = 10;

/**
 * Horizontal half-extent (px, world-space) of the shield ring measured
 * from the boss centre. Sized just outside `VOID_MOTHERSHIP_WIDTH / 2`
 * (= 60) so nodes orbit clearly outside the hull silhouette without
 * pushing them off-screen during the HOLD anchor.
 */
export const VOID_MOTHERSHIP_SHIELD_RADIUS_X = 72;

/**
 * Vertical half-extent (px, world-space) of the shield ring measured
 * from the boss centre. Slightly inside `VOID_MOTHERSHIP_HEIGHT / 2`
 * (= 48) so the top/bottom nodes overlap the hull edge rather than
 * floating in empty space above and below it.
 */
export const VOID_MOTHERSHIP_SHIELD_RADIUS_Y = 44;

/**
 * Angular half-width (radians) of each shield node's *gameplay* coverage
 * around its orbit centre — i.e. a hit whose angle from the boss centre
 * is within `[nodeAngle - halfWidth, nodeAngle + halfWidth]` of any node
 * is blocked while SHIELDED; everything else passes through the gap
 * between nodes and lands damage. With `..._NODE_COUNT = 4` each wedge
 * spans 90°; setting half-width to π/8 (22.5°) gives each node a 45°
 * arc of coverage and leaves four 45° gaps between nodes — a forgiving
 * timing window paired with the existing 1 rad/s ring rotation
 * (~57°/s) so any fixed firing angle becomes hittable within ≈0.8 s.
 *
 * Independent of `..._NODE_SIZE` (which is the rendered AABB size and
 * is fixed in px, not angular). The renderer keeps painting indicative
 * 10×10 squares; the gameplay gate is angular and lives in
 * `shieldGate.ts`.
 */
export const VOID_MOTHERSHIP_SHIELD_NODE_HALF_ANGULAR_WIDTH_RAD =
  Math.PI / 8;

/**
 * Duration (s) of the boss hit-flash visual when an EXPOSED-window
 * projectile lands and HP is actually decremented. Renderer paints the
 * boss hull in a brief near-white flash colour for this window then
 * reverts to the default fill. Short enough that back-to-back hits at
 * the 0.15 s ship-laser cadence still produce visible distinct flashes
 * (i.e. the flash is shorter than the cooldown, so a flash always ends
 * before the next bolt can land). Shielded hits do NOT trigger the
 * flash — the gate is "HP actually decremented", which matches the
 * existing `takeDamage()` shield-no-op.
 */
export const VOID_MOTHERSHIP_HIT_FLASH_DURATION_S = 0.08;
